import { PRODUCT_MAIL_BASE_PATH, PRODUCT_MAIL_LAST_MAILBOX_COOKIE } from '@/constants/productMail';

import type { ProductMailboxMessage, ProductMailboxSummary, ProductMailboxThreadResponse, ProductMailboxThreadsResponse } from './types';

export type ProductMailRouteState =
    | { kind: 'index' }
    | { kind: 'mailbox'; mailboxSlug: string }
    | { kind: 'thread'; mailboxSlug: string; threadCode: string; messageCode: string | null }
    | { kind: 'compose'; mailboxSlug: string }
    | { kind: 'reply'; mailboxSlug: string; threadCode: string; messageCode: string }
    | { kind: 'forward'; mailboxSlug: string; threadCode: string; messageCode: string };

const parseShortCode = (value: string | undefined) => {
    const normalized = decodeURIComponent(String(value || '').trim());
    return /^[A-Za-z0-9_-]{16}$/.test(normalized) ? normalized : null;
};

export const parseProductMailRouteSegments = (segments: string[] = []): ProductMailRouteState | null => {
    if (!segments.length) {
        return { kind: 'index' };
    }

    const [mailboxSlug, ...rest] = segments;

    if (!mailboxSlug) {
        return { kind: 'index' };
    }

    if (!rest.length) {
        return {
            kind: 'mailbox',
            mailboxSlug,
        };
    }

    if (rest.length === 1 && rest[0] === 'compose') {
        return {
            kind: 'compose',
            mailboxSlug,
        };
    }

    if (rest[0] !== 'thread') {
        return null;
    }

    const threadCode = parseShortCode(rest[1]);

    if (!threadCode) {
        return null;
    }

    if (rest.length === 2) {
        return {
            kind: 'thread',
            mailboxSlug,
            threadCode,
            messageCode: null,
        };
    }

    if (rest[2] === 'message' && rest.length === 4) {
        const messageCode = parseShortCode(rest[3]);

        if (!messageCode) {
            return null;
        }

        return {
            kind: 'thread',
            mailboxSlug,
            threadCode,
            messageCode,
        };
    }

    if ((rest[2] === 'reply' || rest[2] === 'forward') && rest.length === 4) {
        const messageCode = parseShortCode(rest[3]);

        if (!messageCode) {
            return null;
        }

        return {
            kind: rest[2],
            mailboxSlug,
            threadCode,
            messageCode,
        };
    }

    return null;
};

export const buildProductMailRoute = ({
    mailboxSlug,
    threadCode,
    messageCode,
    composeIntent,
}: {
    mailboxSlug: string;
    threadCode?: string | null;
    messageCode?: string | null;
    composeIntent?: 'compose' | 'reply' | 'forward' | null;
}) => {
    if (composeIntent === 'compose') {
        return `${PRODUCT_MAIL_BASE_PATH}/${encodeURIComponent(mailboxSlug)}/compose`;
    }

    if (threadCode && composeIntent === 'reply' && messageCode) {
        return `${PRODUCT_MAIL_BASE_PATH}/${encodeURIComponent(mailboxSlug)}/thread/${threadCode}/reply/${messageCode}`;
    }

    if (threadCode && composeIntent === 'forward' && messageCode) {
        return `${PRODUCT_MAIL_BASE_PATH}/${encodeURIComponent(mailboxSlug)}/thread/${threadCode}/forward/${messageCode}`;
    }

    if (threadCode && messageCode) {
        return `${PRODUCT_MAIL_BASE_PATH}/${encodeURIComponent(mailboxSlug)}/thread/${threadCode}/message/${messageCode}`;
    }

    if (threadCode) {
        return `${PRODUCT_MAIL_BASE_PATH}/${encodeURIComponent(mailboxSlug)}/thread/${threadCode}`;
    }

    return `${PRODUCT_MAIL_BASE_PATH}/${encodeURIComponent(mailboxSlug)}`;
};

export { PRODUCT_MAIL_LAST_MAILBOX_COOKIE };

export const resolveMailboxBySlug = ({ mailboxes, mailboxSlug }: { mailboxes: ProductMailboxSummary[]; mailboxSlug: string | null | undefined }) => {
    if (!mailboxSlug) {
        return null;
    }

    return mailboxes.find((mailbox) => mailbox.slug === mailboxSlug) ?? null;
};

export const resolveInitialProductMailSelection = ({
    threadPayload,
    requestedMessageCode,
}: {
    threadPayload: ProductMailboxThreadResponse | null;
    requestedMessageCode: string | null;
}) => {
    const messages = threadPayload?.messages ?? [];

    if (!messages.length) {
        return null;
    }

    const selectedMessage = requestedMessageCode ? (messages.find((message) => message.shortCode === requestedMessageCode) ?? null) : null;

    if (selectedMessage) {
        return selectedMessage.id;
    }

    return messages[messages.length - 1]?.id ?? null;
};

export const getThreadPreviewLabel = ({ thread }: { thread: ProductMailboxThreadsResponse['threads'][number] }) => {
    const fromLabel = thread.latestFrom[0]?.name?.trim() || thread.latestFrom[0]?.address || 'Unknown sender';
    return `${fromLabel}: ${thread.preview}`;
};

export const formatProductMailAddresses = (addresses: Array<{ name: string | null; address: string }>) => {
    if (!addresses.length) {
        return '—';
    }

    return addresses
        .map((address) => {
            const name = address.name?.trim();
            return name ? `${name} <${address.address}>` : address.address;
        })
        .join(', ');
};

export const buildProductMailReplyDraft = ({ message }: { message: ProductMailboxMessage }) => ({
    to: message.from[0]?.address ?? '',
    cc: '',
    bcc: '',
    subject: message.subject?.startsWith('Re: ') ? message.subject : `Re: ${message.subject ?? '(no subject)'}`,
    text: '',
    replyToMessageId: message.id,
    forwardSourceMessageId: null,
});

export const buildProductMailForwardDraft = ({ message }: { message: ProductMailboxMessage }) => ({
    to: '',
    cc: '',
    bcc: '',
    subject: message.subject?.startsWith('Fwd: ') ? message.subject : `Fwd: ${message.subject ?? '(no subject)'}`,
    text: [
        '',
        '---------- Forwarded message ----------',
        `From: ${formatProductMailAddresses(message.from)}`,
        `Date: ${message.sentAt ?? 'Unknown date'}`,
        `Subject: ${message.subject ?? '(no subject)'}`,
        `To: ${formatProductMailAddresses(message.to)}`,
        '',
        message.text,
    ].join('\n'),
    replyToMessageId: null,
    forwardSourceMessageId: message.id,
});
